// ReceiveInventory.jsx
// Streamlined barcode-first workflow for receiving new inventory

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import InventoryAPI from "../../../services/inventoryService";

const DESKTOP_BREAKPOINT = 1024;

class ReceiveInventory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Barcode entry
            barcode: "",
            barcodeError: "",
            existingItem: null,
            existingItems: [], // All items matching barcode (for multiple products)

            // Scanner modal (mobile only)
            showScanner: false,
            scannerError: null,

            // Product selection (cascading: Category → Brand → Product)
            categories: [],
            products: [],
            selectedCategoryId: "",
            selectedBrand: "",
            selectedProductName: "",
            selectedProductId: "",

            // Units of measure
            units: [],

            // Inline creation states
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProductName: '',

            // Quantity
            quantity: "",

            // UI state
            step: 'barcode',
            submitting: false,
            loading: true,
            successMessage: "",

            // Responsive
            isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT
        };

        this.html5QrCode = null;
    }

    async componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        await this.loadInitialData();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        this.stopScanner();
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= DESKTOP_BREAKPOINT });
    };

    isMobile = () => {
        return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    };


    loadInitialData = async () => {
        try {
            const [categories, products, units] = await Promise.all([
                InventoryAPI.getServiceCategories(),
                InventoryAPI.getProducts(this.props.typeId),
                InventoryAPI.getUnitsOfMeasure()
            ]);

            this.setState({
                categories: categories || [],
                products: products || [],
                units: units || [],
                loading: false
            });
        } catch (error) {
            console.error('Error loading initial data:', error);
            this.setState({ loading: false });
        }
    };

    // =========================================================================
    // Barcode Handling
    // =========================================================================

    handleBarcodeChange = (e) => {
        this.setState({
            barcode: e.target.value,
            barcodeError: "",
            existingItem: null
        });
    };

    handleBarcodeSubmit = async (e) => {
        e.preventDefault();
        const { barcode } = this.state;

        if (!barcode.trim()) {
            this.setState({ barcodeError: "Please enter a barcode" });
            return;
        }

        this.setState({ submitting: true, barcodeError: "" });

        try {
            // Pass installerId to scope barcode lookup to this installer only
            const result = await InventoryAPI.lookupBarcode(barcode.trim(), this.props.typeId);

            // API returns { found, multiple, items: [...], data: {...} }
            if (result && result.found && result.items && result.items.length > 0) {
                // Barcode exists - use first item to pre-populate product info
                // (user can confirm or change product selection)
                const existingItem = result.items[0];
                this.setState({
                    existingItem: existingItem,
                    existingItems: result.items, // Store all items in case multiple
                    step: 'product',
                    submitting: false,
                    // Pre-select the existing product's category, brand, and name
                    selectedCategoryId: existingItem.category_id ? String(existingItem.category_id) : "",
                    selectedBrand: existingItem.brand || "",
                    selectedProductName: existingItem.product_name || existingItem.name || "",
                    selectedProductId: existingItem.product_id ? String(existingItem.product_id) : ""
                });
            } else {
                // Barcode not found - proceed to product selection
                this.setState({
                    step: 'product',
                    submitting: false,
                    existingItem: null,
                    existingItems: []
                });
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                // 404 means barcode not found - proceed to product selection
                this.setState({
                    step: 'product',
                    submitting: false,
                    existingItem: null,
                    existingItems: []
                });
            } else {
                console.error('Error checking barcode:', error);
                this.setState({
                    barcodeError: "Error checking barcode. Please try again.",
                    submitting: false
                });
            }
        }
    };

    // =========================================================================
    // Barcode Scanner (Mobile)
    // =========================================================================

    handleOpenScanner = () => {
        this.setState({ showScanner: true, scannerError: null }, () => {
            setTimeout(() => this.startScanner(), 500);
        });
    };

    handleCloseScanner = () => {
        this.stopScanner();
        this.setState({ showScanner: false, scannerError: null });
    };

    startScanner = () => {
        const scannerId = 'barcode-scanner-reader';
        const scannerElement = document.getElementById(scannerId);

        if (!scannerElement) {
            console.error('Scanner element not found');
            return;
        }

        const formatsToSupport = [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.QR_CODE
        ];

        this.html5QrCode = new Html5Qrcode(scannerId, { formatsToSupport });

        this.html5QrCode.start(
            { facingMode: "environment" },
            {
                fps: 10,
                qrbox: { width: 280, height: 100 }
            },
            this.handleScanSuccess,
            this.handleScanError
        ).catch(err => {
            console.error('Error starting scanner:', err);
            this.setState({ scannerError: 'Unable to access camera. Please check permissions.' });
        });
    };

    stopScanner = () => {
        if (this.html5QrCode && this.html5QrCode.isScanning) {
            this.html5QrCode.stop().catch(err => {
                console.error('Error stopping scanner:', err);
            });
        }
    };

    handleScanSuccess = (decodedText, decodedResult) => {
        console.log('Barcode scanned:', decodedText);

        this.stopScanner();
        this.setState({
            barcode: decodedText,
            showScanner: false
        }, () => {
            this.handleBarcodeSubmit({ preventDefault: () => {} });
        });
    };

    handleScanError = (errorMessage) => {
        // Silently ignore "not found" errors - these are normal during scanning
    };

    // =========================================================================
    // Product Selection (Cascading: Category → Brand → Product)
    // =========================================================================

    // Get unique brands filtered by selected category
    getUniqueBrands = () => {
        const { products, selectedCategoryId } = this.state;
        if (!selectedCategoryId) return [];

        const filteredProducts = products.filter(p =>
            String(p.category_id) === String(selectedCategoryId)
        );

        const brands = filteredProducts
            .map(p => p.brand)
            .filter(brand => brand && brand.trim())
            .filter((brand, index, self) => self.indexOf(brand) === index)
            .sort();
        return brands;
    };

    // Get unique product names filtered by category and brand
    getUniqueProductNames = () => {
        const { products, selectedCategoryId, selectedBrand } = this.state;
        if (!selectedCategoryId || !selectedBrand) return [];

        const filteredProducts = products.filter(p =>
            String(p.category_id) === String(selectedCategoryId) &&
            p.brand === selectedBrand
        );

        const names = filteredProducts
            .map(p => p.product_name || p.name)
            .filter(name => name && name.trim())
            .filter((name, index, self) => self.indexOf(name) === index)
            .sort();
        return names;
    };

    // Find the product ID from category, brand, and product name
    findProductId = () => {
        const { products, selectedCategoryId, selectedBrand, selectedProductName } = this.state;
        if (!selectedCategoryId || !selectedBrand || !selectedProductName) return null;

        const product = products.find(p =>
            String(p.category_id) === String(selectedCategoryId) &&
            p.brand === selectedBrand &&
            (p.product_name || p.name) === selectedProductName
        );

        return product ? (product.product_id || product.id) : null;
    };

    handleCategoryChange = (e) => {
        const categoryId = e.target.value;

        this.setState({
            selectedCategoryId: categoryId,
            selectedBrand: "",
            selectedProductName: "",
            selectedProductId: "",
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProductName: ''
        });
    };

    handleBrandChange = (e) => {
        const value = e.target.value;

        if (value === 'add_new') {
            this.setState({
                showAddBrand: true,
                selectedBrand: "",
                selectedProductName: "",
                selectedProductId: "",
                showAddProduct: false,
                newProductName: ''
            });
        } else {
            this.setState({
                selectedBrand: value,
                showAddBrand: false,
                newBrand: '',
                selectedProductName: "",
                selectedProductId: "",
                showAddProduct: false,
                newProductName: ''
            });
        }
    };

    handleNewBrandChange = (e) => {
        const value = e.target.value;
        this.setState({
            newBrand: value,
            selectedBrand: value
        });
    };

    handleCancelNewBrand = () => {
        this.setState({
            showAddBrand: false,
            newBrand: '',
            selectedBrand: ""
        });
    };

    handleProductNameChange = (e) => {
        const value = e.target.value;

        if (value === 'add_new') {
            this.setState({
                showAddProduct: true,
                selectedProductName: "",
                selectedProductId: ""
            });
        } else {
            // Find the product ID for this selection
            const { products, selectedCategoryId, selectedBrand } = this.state;
            const product = products.find(p =>
                String(p.category_id) === String(selectedCategoryId) &&
                p.brand === selectedBrand &&
                (p.product_name || p.name) === value
            );
            const productId = product ? (product.product_id || product.id) : "";

            this.setState({
                selectedProductName: value,
                selectedProductId: String(productId),
                showAddProduct: false,
                newProductName: ''
            });
        }
    };

    handleNewProductNameChange = (e) => {
        const value = e.target.value;
        this.setState({
            newProductName: value,
            selectedProductName: value
        });
    };

    handleCancelNewProduct = () => {
        this.setState({
            showAddProduct: false,
            newProductName: '',
            selectedProductName: ""
        });
    };

    // =========================================================================
    // Receive Submission
    // =========================================================================

    handleQuantityChange = (e) => {
        this.setState({ quantity: e.target.value });
    };

    handleReceiveSubmit = async (e) => {
        e.preventDefault();
        const {
            barcode, selectedProductId, selectedProductName, selectedBrand,
            selectedCategoryId, quantity, showAddBrand, showAddProduct, categories
        } = this.state;

        const isNewProduct = showAddBrand || showAddProduct;

        if (!quantity || parseFloat(quantity) <= 0) {
            alert('Please enter a valid quantity');
            return;
        }

        this.setState({ submitting: true });

        try {
            let productId = selectedProductId;

            // If this is a new product, create it first
            if (isNewProduct && !selectedProductId) {
                if (!selectedProductName || !selectedProductName.trim()) {
                    alert('Product name is required');
                    this.setState({ submitting: false });
                    return;
                }

                if (!selectedBrand || !selectedBrand.trim()) {
                    alert('Brand is required');
                    this.setState({ submitting: false });
                    return;
                }

                // Check for duplicate product name + brand combination (case-insensitive)
                const newName = selectedProductName.trim().toLowerCase();
                const newBrand = selectedBrand.trim().toLowerCase();
                const duplicate = this.state.products.find(p => {
                    const existingName = (p.product_name || p.name || '').toLowerCase();
                    const existingBrand = (p.brand || '').toLowerCase();
                    return existingName === newName && existingBrand === newBrand;
                });
                if (duplicate) {
                    alert(`A product named "${selectedProductName.trim()}" with brand "${selectedBrand.trim()}" already exists.`);
                    this.setState({ submitting: false });
                    return;
                }

                // Get unitId from the selected category's default unit
                let unitId = null;
                if (selectedCategoryId) {
                    const selectedCategory = categories.find(c =>
                        String(c.category_id) === String(selectedCategoryId)
                    );
                    if (selectedCategory) {
                        unitId = selectedCategory.default_unit_id || selectedCategory.defaultUnitId;
                    }
                }

                // Create the new product
                const productData = {
                    name: selectedProductName.trim(),
                    brand: selectedBrand.trim(),
                    categoryId: parseInt(selectedCategoryId, 10),
                    unitId: unitId ? parseInt(unitId, 10) : null,
                    reorderPoint: 0,
                    installerId: this.props.typeId
                };

                const result = await InventoryAPI.createProduct(productData);
                productId = result.id || result.product_id;
            }

            if (!productId) {
                alert('Please select a product');
                this.setState({ submitting: false });
                return;
            }

            // Receive the inventory
            const receiveData = {
                productId: parseInt(productId, 10),
                installerId: parseInt(this.props.typeId, 10),
                barcode: barcode.trim(),
                quantity: parseFloat(quantity)
            };

            await InventoryAPI.receiveInventory(receiveData);

            // Redirect immediately to inventory dashboard
            this.props.history.push('/inventory');

        } catch (error) {
            console.error('Error receiving inventory:', error);
            alert('Error receiving inventory. Please try again.');
            this.setState({ submitting: false });
        }
    };

    handleBackToBarcode = () => {
        this.setState({
            step: 'barcode',
            selectedCategoryId: "",
            selectedBrand: "",
            selectedProductName: "",
            selectedProductId: "",
            quantity: "",
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProductName: '',
            existingItem: null,
            existingItems: []
        });
    };

    // =========================================================================
    // Render
    // =========================================================================

    renderBarcodeStep() {
        const { barcode, barcodeError, existingItem, submitting } = this.state;
        const isMobile = this.isMobile();

        return (
            <div className="receive-step">
                <h5 className="step-title">Step 1: Enter Barcode</h5>

                <Form onSubmit={this.handleBarcodeSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Barcode</Form.Label>
                        <div className="barcode-input-group">
                            <Form.Control
                                type="text"
                                value={barcode}
                                onChange={this.handleBarcodeChange}
                                placeholder="Scan or enter barcode..."
                                isInvalid={!!barcodeError}
                                autoFocus
                            />
                            {isMobile && (
                                <Button
                                    variant="outline-secondary"
                                    className="scan-btn"
                                    onClick={this.handleOpenScanner}
                                    title="Scan barcode"
                                >
                                    <i className="fa fa-camera"></i>
                                </Button>
                            )}
                        </div>
                        {barcodeError && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {barcodeError}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    {existingItem && (
                        <Alert variant="warning">
                            <strong>Item already in system:</strong>
                            <br />
                            Product: {existingItem.product_name || 'Unknown'}
                            <br />
                            Quantity: {existingItem.quantity_remaining || 0}
                        </Alert>
                    )}

                    <Button
                        type="submit"
                        variant="primary"
                        disabled={submitting || !barcode.trim()}
                        className="w-100"
                    >
                        {submitting ? (
                            <><i className="fa fa-spinner fa-spin mr-2"></i>Checking...</>
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </Form>
            </div>
        );
    }

    // Helper to get unit name from unit_id
    getUnitName = (unitId) => {
        const { units } = this.state;
        const unit = units.find(u => String(u.unit_id) === String(unitId));
        return unit ? unit.unit_name : '';
    };

    // Helper to get unit name from selected category's default unit
    getSelectedCategoryUnitName = () => {
        const { categories, selectedCategoryId } = this.state;
        if (!selectedCategoryId) return '';

        const category = categories.find(c => String(c.category_id) === String(selectedCategoryId));
        if (category) {
            const unitId = category.default_unit_id || category.defaultUnitId;
            if (unitId) {
                return this.getUnitName(unitId);
            }
        }
        return '';
    };

    renderProductStep() {
        const {
            categories,
            selectedCategoryId, selectedBrand, selectedProductName, selectedProductId,
            showAddBrand, newBrand, showAddProduct, newProductName,
            quantity, submitting
        } = this.state;

        const existingBrands = this.getUniqueBrands();
        const existingProductNames = this.getUniqueProductNames();
        const hasBrandSelected = selectedBrand && selectedBrand.trim();
        const hasProductSelected = selectedProductName && selectedProductName.trim();
        const isNewProduct = showAddBrand || showAddProduct;
        const selectedUnitName = this.getSelectedCategoryUnitName();

        // Show quantity field when: existing product selected OR new product name entered
        const showQuantityField = selectedProductId || (isNewProduct && hasProductSelected);

        return (
            <div className="receive-step">
                <h5 className="step-title">Step 2: Select Product & Quantity</h5>

                <Button
                    variant="link"
                    className="back-link p-0 mb-3"
                    onClick={this.handleBackToBarcode}
                >
                    <i className="fa fa-arrow-left mr-2"></i>Back to barcode
                </Button>

                {/* Show notice if barcode was found - user can confirm or change product */}
                {this.state.existingItem && (
                    <Alert variant="info" className="mb-3">
                        <i className="fa fa-info-circle mr-2"></i>
                        <strong>Barcode found:</strong> This barcode is currently linked to{' '}
                        <strong>{this.state.existingItem.brand} - {this.state.existingItem.product_name || this.state.existingItem.name}</strong>.
                        Confirm below or select a different product.
                    </Alert>
                )}

                <Form onSubmit={this.handleReceiveSubmit}>
                    {/* Category Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedCategoryId}
                            onChange={this.handleCategoryChange}
                        >
                            <option value="">Select a category...</option>
                            {[...new Map(categories.map(c => [c.category_id, c])).values()]
                                .sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''))
                                .map(cat => (
                                    <option key={cat.category_id} value={cat.category_id}>
                                        {cat.category_name}
                                    </option>
                                ))}
                        </Form.Control>
                    </Form.Group>

                    {/* Brand Selection - filtered by category */}
                    {selectedCategoryId && (
                        <Form.Group className="mb-3">
                            <Form.Label>Brand</Form.Label>
                            {!showAddBrand ? (
                                <Form.Control
                                    as="select"
                                    value={selectedBrand}
                                    onChange={this.handleBrandChange}
                                >
                                    <option value="">
                                        {existingBrands.length === 0
                                            ? 'No brands for this category - add new'
                                            : 'Select a brand...'}
                                    </option>
                                    {existingBrands.map(brand => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                    <option value="add_new">+ Add New Brand</option>
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={newBrand}
                                        onChange={this.handleNewBrandChange}
                                        placeholder="Enter new brand name"
                                        autoFocus
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.handleCancelNewBrand}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form.Group>
                    )}

                    {/* Product Name Selection - filtered by category and brand */}
                    {hasBrandSelected && (
                        <Form.Group className="mb-3">
                            <Form.Label>Product Name</Form.Label>
                            {!showAddProduct ? (
                                <Form.Control
                                    as="select"
                                    value={selectedProductName}
                                    onChange={this.handleProductNameChange}
                                >
                                    <option value="">
                                        {existingProductNames.length === 0
                                            ? 'No products for this brand - add new'
                                            : 'Select a product...'}
                                    </option>
                                    {existingProductNames.map(name => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                    <option value="add_new">+ Add New Product</option>
                                </Form.Control>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <Form.Control
                                        type="text"
                                        value={newProductName}
                                        onChange={this.handleNewProductNameChange}
                                        placeholder="Enter new product name"
                                        autoFocus
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ml-2"
                                        onClick={this.handleCancelNewProduct}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </Form.Group>
                    )}

                    {/* Quantity - show when product is selected OR new product name entered */}
                    {showQuantityField && (
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Quantity on Item
                                {selectedUnitName && <small className="text-muted ml-1">({selectedUnitName})</small>}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={quantity}
                                onChange={this.handleQuantityChange}
                                placeholder={selectedUnitName ? `Enter ${selectedUnitName.toLowerCase()}` : 'Enter quantity'}
                            />
                            <Form.Text className="text-muted">
                                How many {selectedUnitName ? selectedUnitName.toLowerCase() : 'units'} are on this roll/item?
                            </Form.Text>
                        </Form.Group>
                    )}

                    {/* Submit */}
                    {showQuantityField && (
                        <Button
                            type="submit"
                            variant="success"
                            disabled={submitting || !quantity}
                            className="w-100"
                        >
                            {submitting ? (
                                <><i className="fa fa-spinner fa-spin mr-2"></i>Receiving...</>
                            ) : (
                                'Receive Inventory'
                            )}
                        </Button>
                    )}
                </Form>
            </div>
        );
    }

    renderScannerModal() {
        const { showScanner, scannerError } = this.state;

        return (
            <Modal
                show={showScanner}
                onHide={this.handleCloseScanner}
                centered
                size="lg"
                className="barcode-scanner-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-barcode mr-2"></i>
                        Scan Barcode
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {scannerError ? (
                        <div className="p-4 text-center text-danger">
                            <i className="fa fa-exclamation-triangle fa-2x mb-2"></i>
                            <p>{scannerError}</p>
                        </div>
                    ) : (
                        <div className="scanner-container">
                            <div id="barcode-scanner-reader" style={{ width: '100%' }}></div>
                            <div className="text-center p-3">
                                <p className="text-muted mb-2">
                                    <i className="fa fa-lightbulb mr-2"></i>
                                    Position the barcode within the box
                                </p>
                                <small className="text-muted">
                                    Ensure good lighting and hold the camera steady
                                </small>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseScanner}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { step, successMessage, loading, isDesktop } = this.state;

        if (loading) {
            return (
                <div className="receive-container">
                    <div className="text-center py-5">
                        <i className="fa fa-spinner fa-spin fa-2x"></i>
                        <p className="mt-3">Loading...</p>
                    </div>
                </div>
            );
        }

        return (
            <React.Fragment>
                <div className={`receive-container ${isDesktop ? 'desktop' : 'mobile'}`}>
                    <div className="receive-header">
                        <div className="header-row">
                            <h4>Receive Inventory</h4>
                            <Link to="/inventory" className="close-btn" title="Return to inventory">
                                <i className="fa fa-times"></i>
                            </Link>
                        </div>
                        <p className="text-muted">
                            Scan or enter a barcode to add new inventory items
                        </p>
                    </div>

                    {successMessage && (
                        <Alert variant="success" dismissible onClose={() => this.setState({ successMessage: "" })}>
                            <i className="fa fa-check-circle mr-2"></i>
                            {successMessage}
                        </Alert>
                    )}

                    <div className="receive-card">
                        {step === 'barcode' && this.renderBarcodeStep()}
                        {step === 'product' && this.renderProductStep()}
                    </div>
                </div>

                {this.renderScannerModal()}

                <style>{`
                    .receive-container {
                        max-width: 600px;
                        margin: 0 auto;
                        padding: 24px;
                        background: #f8fafc;
                        min-height: 100vh;
                    }

                    .receive-container.mobile {
                        padding: 16px;
                    }

                    .receive-header {
                        margin-bottom: 24px;
                    }

                    .receive-header .header-row {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }

                    .receive-header h4 {
                        margin-bottom: 8px;
                        color: #374151;
                    }

                    .close-btn {
                        width: 32px;
                        height: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        color: #6b7280;
                        text-decoration: none;
                        transition: all 0.2s ease;
                    }

                    .close-btn:hover {
                        background: #e5e7eb;
                        color: #374151;
                        text-decoration: none;
                    }

                    .receive-card {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 24px;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }

                    .step-title {
                        color: #374151;
                        margin-bottom: 20px;
                        padding-bottom: 12px;
                        border-bottom: 1px solid #e5e7eb;
                    }

                    .barcode-input-group {
                        display: flex;
                        gap: 8px;
                    }

                    .barcode-input-group .form-control {
                        flex: 1;
                    }

                    .scan-btn {
                        padding: 8px 16px;
                    }

                    .back-link {
                        color: #6b7280;
                        text-decoration: none;
                    }

                    .back-link:hover {
                        color: #374151;
                    }

                    .inline-product-form {
                        background: #f8fafc;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        padding: 16px;
                        margin-bottom: 16px;
                    }

                    .inline-product-form h6 {
                        color: #374151;
                        margin-bottom: 12px;
                    }

                    .inline-product-actions {
                        margin-top: 12px;
                        display: flex;
                        justify-content: flex-end;
                        gap: 8px;
                    }

                    .barcode-scanner-modal .modal-body {
                        min-height: 350px;
                    }

                    #barcode-scanner-reader {
                        border: none !important;
                    }

                    #barcode-scanner-reader video {
                        width: 100% !important;
                        border-radius: 4px;
                    }

                    #barcode-scanner-reader__scan_region {
                        min-height: 300px;
                    }

                    #barcode-scanner-reader__dashboard_section {
                        display: none !important;
                    }
                `}</style>
            </React.Fragment>
        );
    }
}

export default ReceiveInventory;
